import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { Store } from '@ngxs/store';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FreeFilterInputComponent } from '../../utils/component/free-filter-input/free-filter-input.component';
import { AsyncPipe } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoModule } from '@jsverse/transloco';
import { messageTypes, WidgetMessageService } from '../../core/services/widget-message.service';
import { MatDialog } from '@angular/material/dialog';
import { LearningSectionEntityService } from '../services/learning-section.entity.service';
import { ConsumerEntityService } from '../../consumer/services/consumer.entity.service';
import { SubjectEntityService } from '../../subject/services/subject.entity.service';
import {
  LearningSectionContainerEntityService
} from '../learning-section-container/services/learning-section-container.entity.service';
import {
  LearningSectionLayoutEntityService
} from '../learning-section-layout/services/learning-section-layout.entity.service';
import { BehaviorSubject, combineLatest, firstValueFrom, from, Observable, of, switchMap, tap } from 'rxjs';
import { LearningSectionEntity } from '../learning-section-entity';
import { LearningUnitEntity } from '../../learning-unit/learning-unit-entity';
import { ConsumerEntity } from '../../consumer/consumer.entity';
import { SubjectEntity } from '../../subject/subject.entity';
import { LearningSectionContainerEntity } from '../learning-section-container/learning-section-container-entity';
import { LearningSectionLayoutEntity } from '../learning-section-layout/learning-section-layout-entity';
import { map } from 'rxjs/operators';
import { LearningUnitEntityService } from '../../learning-unit/services/learning-unit.entity.service';
import {
  MultiComboEntitySearchComponent
} from '../../utils/component/multi-combo-entity-search/multi-combo-entity-search.component';
import { TableLoadingSpinnerComponent } from '../../utils/component/table-loading-spinner/table-loading-spinner';
import { EntityStateModel, InvalidateLoadedQueries } from '../../core/state/entity.state';
import { makeQueryKey } from '../../utils/entity-states/query-key';
import {
  FormDialogLearningSectionComponent
} from '../form-dialog-learning-section/form-dialog-learning-section.component';
import {
  ConfirmDialogRawComponent,
  SimpleConfirmData
} from '../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import {
  FormDialogLearningSectionContainerComponent
} from '../learning-section-container/form-dialog-learning-section-container/form-dialog-learning-section-container.component';
import { LocalStorageService } from '../../core/services/local-storage.service';
import { BaseFilterComponent } from '../../utils/component/base-filter-component/base-filter-component';
import { LiveAnnouncer } from '@angular/cdk/a11y';

@Component({
  selector: 'pixl-main-learning-section',
  imports: [
    MatIcon,
    MatSortModule,
    MatIconModule,
    MatTableModule,
    MatButtonModule,
    MatTooltip,
    FreeFilterInputComponent,
    MatSelectModule,
    AsyncPipe,
    TranslocoModule,
    MultiComboEntitySearchComponent,
    MatPaginator,
    TableLoadingSpinnerComponent
  ],
  templateUrl: './main-learning-section.component.html',
  styleUrl: './main-learning-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MainLearningSectionComponent extends BaseFilterComponent implements OnInit, AfterViewInit {
  private store = inject(Store);
  private widgetService = inject(WidgetMessageService);
  private dialog = inject(MatDialog);
  private _liveAnnouncer = inject(LiveAnnouncer);

  private learningSectionService = inject(LearningSectionEntityService);
  private learningUnitService = inject(LearningUnitEntityService);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private learningSectionContainerService = inject(LearningSectionContainerEntityService);
  private learningSectionLayoutService = inject(LearningSectionLayoutEntityService);
  private localStorageService = inject(LocalStorageService);
  private labelLearningUnits: string[] = [];

  public readonly coloredButtonsEnabled = this.localStorageService.coloredButtonsEnabled;
  learningSections$!: Observable<LearningSectionEntity[]>;
  deletedIds$ = new BehaviorSubject<string[]>([]);
  private readonly pageChange$ = toObservable(this.pageChange);

  learningUnits$!: Observable<LearningUnitEntity[]>;
  consumers$!: Observable<ConsumerEntity[]>;
  subjects$!: Observable<SubjectEntity[]>;
  learningSectionContainers$!: Observable<LearningSectionContainerEntity[]>;
  learningSectionLayouts$!: Observable<LearningSectionLayoutEntity[]>;

  selectedLearningUnits: LearningUnitEntity[] = [];
  currentPageLearningUnits = 1;
  pageSizeLearningUnits = 20;
  searchQueryLearningUnits: string | null = null;

  private refresh$ = new BehaviorSubject<void>(undefined);

  public displayedColumns: string[] = ['actions', 'id', 'name', 'title', 'learningSectionType', 'consumer', 'subjects', 'learningUnits', 'learningSectionLayout', 'showCopyright', 'version'];

  totalItems = 1000;
  private sortString = 'name';

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.loadRelationData();

    this.learningSections$ = combineLatest([
      this.pageChange$,
      this.refresh$,
      this.deletedIds$
    ]).pipe(
      switchMap(([{ pageIndex, pageSize }, , deletedIds]) => {
        const query: Record<string, string | number> = {
          'include': 'consumer,subjects,learningUnits,learningSectionLayout,learningSectionContainers',
          'page[size]': pageSize,
          'page[number]': pageIndex + 1,
          'sort': this.sortString,
        };

        if (this.freeFilter?.trim()?.length >= this.freeFilterLen) {
          query['filter[idOrNameLike]'] = this.freeFilter.trim();
        }

        if (this.consumerFilter?.length > 0) {
          query['filter[consumer][id]'] = this.consumerFilter.join(',');
        }

        if (this.subjectFilter?.length > 0) {
          query['filter[subjects][name]'] = this.subjectFilter.join(',');
        }

        if (this.learningUnitFilter?.length > 0) {
          query['filter[learningUnits][id]'] = this.learningUnitFilter.join(',');
        }

        return from(this.learningSectionService.ensureLoaded(query)).pipe(
          switchMap(() => this.store.select(this.learningSectionService.allByQuery(query))),
          map((learningSections: LearningSectionEntity[]) => learningSections.filter(m => !deletedIds.includes(m.id))),
          tap(() => {
            const state = this.store.selectSnapshot(this.learningSectionService.stateClass) as EntityStateModel<LearningSectionEntity>;
            const queryKey = makeQueryKey(query);
            this.totalItems = state?.meta?.[queryKey]?.page?.total ?? 0;
          })
        );
      })
    );
  }

  ngAfterViewInit(): void {
    this.paginator().page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: PageEvent) => {
      this.pageChange.set({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  private loadRelationData(): void {
    const learningUnitQuery = { 'page[size]': this.pageSizeLearningUnits, 'page[number]': this.currentPageLearningUnits, 'sort': 'title' };

    this.learningUnits$ = from(
      this.learningUnitService.ensureLoaded(learningUnitQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.learningUnitService.allByQuery(learningUnitQuery)).pipe(
          map(paginated =>
            [...this.selectedLearningUnits, ...paginated].filter(
              (lu, index, self) => index === self.findIndex(s => s.id === lu.id)
            )
          )
        )
      )
    );

    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(
      this.consumerService.ensureLoaded(consumerQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.consumerService.allByQuery(consumerQuery))
      )
    );

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(
      this.subjectService.ensureLoaded(subjectQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.subjectService.allByQuery(subjectQuery))
      )
    );

    const learningSectionLayoutQuery = { sort: 'name' };
    this.learningSectionLayouts$ = from(
      this.learningSectionLayoutService.ensureLoaded(learningSectionLayoutQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.learningSectionLayoutService.allByQuery(learningSectionLayoutQuery))
      )
    );

    this.learningSectionContainers$ = from(
      this.learningSectionContainerService.ensureLoaded({})
    ).pipe(
      switchMap(() =>
        this.store.select(this.learningSectionContainerService.allByQuery({}))
      )
    );
  }

  onAdd(): void {
    const dialogRef = this.dialog.open(FormDialogLearningSectionComponent, {
      width: '700px',
      maxWidth: 'none',
      maxHeight: '90vh',
      data: {}
    });

    dialogRef.afterClosed().subscribe(async (result: Partial<LearningSectionEntity> | undefined) => {
      if (result) {
        const newEntityInstance = Object.assign(new LearningSectionEntity(), result);

        await this.learningSectionService.create(newEntityInstance);
        this.store.dispatch(new InvalidateLoadedQueries());
        this.refresh$.next();

        this.showMessage('Lernabschnitt erfolgreich erstellt.');
      }
    });
  }

  onEdit(learningSection: LearningSectionEntity): void {
    this.currentSelectedRow.set(learningSection.id);

    const dialogRef = this.dialog.open(FormDialogLearningSectionComponent, {
      width: '700px',
      maxWidth: 'none',
      maxHeight: '90vh',
      data: { ...learningSection }
    });

    dialogRef.afterClosed().subscribe(async (updatedLearningSection: LearningSectionEntity | undefined) => {
      if (!updatedLearningSection) return;
      await firstValueFrom(this.learningSectionService.update(updatedLearningSection));
      this.refresh$.next();

      this.showMessage('Lernabschnitt erfolgreich editiert.');
    });
  }

  onEditContainer(learningSection: LearningSectionEntity) {
    this.currentSelectedRow.set(learningSection.id);

    const dialogRef = this.dialog.open(FormDialogLearningSectionContainerComponent, {
      width: '1200px',
      maxWidth: 'none',
      maxHeight: '90vh',
      data: { ...learningSection }
    });

    dialogRef.afterClosed().subscribe(async (updatedLearningSection: LearningSectionEntity | undefined) => {
      if (!updatedLearningSection) return;
      await firstValueFrom(this.learningSectionService.update(updatedLearningSection));
      this.refresh$.next();

      this.showMessage('Container erfolgreich editiert.');
    });
  }

  async onCopy(learningSection: LearningSectionEntity) {
    this.currentSelectedRow.set(learningSection.id);

    const confirmed = await firstValueFrom(
      this.dialog.open(ConfirmDialogRawComponent, {
        data: {
          name: String(learningSection.title || learningSection.name),
          headerText: 'Lernabschnitt kopieren',
          afterNameText: 'wirklich kopieren?'
        }
      }).afterClosed()
    );

    if (!confirmed) return;

    try {
      await firstValueFrom(this.learningSectionService.copy(learningSection.id));

      this.store.dispatch(new InvalidateLoadedQueries());
      this.refresh$.next();
      this.showMessage('Lernabschnitt erfolgreich dupliziert.');
    }
    catch (error: unknown) {
      console.error('Copy failed', error);
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      this.showMessage('Fehler beim Kopieren: ' + errorMessage);
    }
  }

  async onDelete(learningSection: LearningSectionEntity) {
    if (!learningSection?.id) {
      console.error('ID is undefined!', learningSection);
      return;
    }

    this.currentSelectedRow.set(learningSection.id);

    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: String(learningSection.title),
            headerText: 'Lernabschnitt löschen',
            afterNameText: 'wirklich löschen?'
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    await firstValueFrom(this.learningSectionService.delete(learningSection.id));

    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.showMessage('Lernabschnitt erfolgreich gelöscht.');
  }

  public announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    }
    else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
    this.sortData(sortState);
  }

  public sortData(sort: Sort): void {
    this.sortString = sort.direction === 'desc' ? `-${sort.active}` : sort.active;
    this.refresh$.next();
  }

  private showMessage(message: string, messageTye: messageTypes = 'mtSuccess'): void {
    this.widgetService.setMessage({
      message,
      messageTye
    });
  }

  getLearningUnitNames(learningUnits: { id: string }[]): string[] {
    if (!learningUnits?.length) return [];

    const state = this.store.selectSnapshot(this.learningUnitService.stateClass);

    return learningUnits.map(learningUnit => {
      const entity = this.learningUnitService.stateClass.byId(state)(learningUnit.id);
      return entity?.title || learningUnit.id;
    });
  }

  getConsumerName(consumerId: string): string {
    if (!consumerId) return '---';

    const state = this.store.selectSnapshot(this.consumerService.stateClass);
    const consumer = this.consumerService.stateClass.byId(state)(consumerId);

    return consumer?.name || consumerId;
  }

  getSubjectNames(subjects: { id: string }[]): string[] {
    if (!subjects?.length) return [];

    const state = this.store.selectSnapshot(this.subjectService.stateClass);

    return subjects.map(subject => {
      const entity = this.subjectService.stateClass.byId(state)(subject.id);
      return entity?.name || subject.id;
    });
  }

  getLearningSectionLayoutName(learningSectionLayoutId: string): string {
    if (!learningSectionLayoutId) return '---';

    const state = this.store.selectSnapshot(this.learningSectionLayoutService.stateClass);
    const learningSectionLayout = this.learningSectionLayoutService.stateClass.byId(state)(learningSectionLayoutId);

    return learningSectionLayout?.name || learningSectionLayoutId;
  }

  receiveLearningUnitData(selected: LearningUnitEntity[] | null) {
    this.selectedLearningUnits = selected ?? [];
    const selectedUnits = (selected ?? []).map(lu => lu.id);
    this.labelLearningUnits = (selected ?? []).map(lu => lu.title);
    this.setLearningUnitFilter(selectedUnits);
  }

  async receiveLearningUnitQuery(query: string) {
    this.searchQueryLearningUnits = query?.trim() || null;

    if (this.searchQueryLearningUnits && this.searchQueryLearningUnits.length > 3) {
      // search mode
      const searchParams = { 'filter[idOrTitleLike]': this.searchQueryLearningUnits };
      await this.learningUnitService.ensureLoaded(searchParams);

      this.learningUnits$ = this.store.select(this.learningUnitService.allByQuery(searchParams)).pipe(
        map(results => results)
      );
    }
    else {
      // default paginated mode
      const learningUnitPageQuery = { 'page[size]': this.pageSizeLearningUnits, 'page[number]': this.currentPageLearningUnits, 'sort': 'title' };
      this.learningUnits$ = this.store.select(this.learningUnitService.allByQuery(learningUnitPageQuery)).pipe(
        map(paginated =>
          [...this.selectedLearningUnits, ...paginated]
            .filter((lu, index, self) => index === self.findIndex(s => s.id === lu.id))
        )
      );
    }
  }

  async loadMoreLearningUnits(event: { pageNumber: number, pageSize: number }) {
    if (!this.searchQueryLearningUnits) {
      const learningUnitPageQuery = {
        'page[size]': event.pageSize,
        'page[number]': event.pageNumber,
        'sort': 'title',
      };

      await this.learningUnitService.ensureLoaded(learningUnitPageQuery);

      const currentLearningUnits = await firstValueFrom(this.learningUnits$ ?? of([]));

      this.learningUnits$ = this.store.select(this.learningUnitService.allByQuery(learningUnitPageQuery)).pipe(
        map(paginated =>
          [
            ...this.selectedLearningUnits,
            ...currentLearningUnits,
            ...paginated,
          ].filter((lu, index, self) => index === self.findIndex(s => s.id === lu.id))
        )
      );
    }
  }

  get learningUnitLabels(): string {
    return this.labelLearningUnits?.length ?
      this.labelLearningUnits.join(', ') :
      'Alle Lerneinheiten';
  }

  trackById(index: number, learningSection: LearningSectionEntity): string {
    return learningSection.id;
  }
}
